import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { subDays } from "date-fns";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { Activity } from "lucide-react";

// Country coordinates for markers
const countryCoordinates: Record<string, [number, number]> = {
  'US': [-95.7129, 37.0902],
  'GB': [-3.4360, 55.3781],
  'CA': [-106.3468, 56.1304],
  'FR': [2.2137, 46.2276],
  'DE': [10.4515, 51.1657],
  'ES': [-3.7492, 40.4637],
  'IT': [12.5674, 41.8719],
  'NL': [5.2913, 52.1326],
  'BE': [4.4699, 50.5039],
  'CH': [8.2275, 46.8182],
  'AU': [133.7751, -25.2744],
  'JP': [138.2529, 36.2048],
  'CN': [104.1954, 35.8617],
  'IN': [78.9629, 20.5937],
  'BR': [-51.9253, -14.2350],
  'MX': [-102.5528, 23.6345],
  'AR': [-63.6167, -38.4161],
  'ZA': [22.9375, -30.5595],
  'EG': [30.8025, 26.8206],
  'MA': [-7.0926, 31.7917],
  'Unknown': [0, 0],
};

interface VisitorMapProps {
  period?: string;
}

export function VisitorMap({ period = "7" }: VisitorMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapboxToken, setMapboxToken] = useState<string>('');

  const { data: analytics } = useQuery({
    queryKey: ["visitor-map", period],
    queryFn: async () => {
      const days = parseInt(period);
      const startDate = subDays(new Date(), days);
      
      const { data, error } = await supabase
        .from("website_analytics")
        .select("country, visitor_id, visited_at")
        .gte("visited_at", startDate.toISOString());

      if (error) throw error;
      return data || [];
    },
  });

  // Real-time subscription
  useRealtimeSubscription("website_analytics", ["visitor-map", period]);

  // Fetch Mapbox token from Supabase secrets
  useEffect(() => {
    const fetchToken = async () => {
      const { data } = await supabase.functions.invoke('get-mapbox-token');
      if (data?.token) {
        setMapboxToken(data.token);
      }
    };
    fetchToken();
  }, []);

  // Aggregate visitor data by country
  const countryData = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!analytics) return;

    const newCountryData = new Map<string, number>();
    analytics.forEach(visit => {
      const country = visit.country || 'Unknown';
      newCountryData.set(country, (newCountryData.get(country) || 0) + 1);
    });

    countryData.current = newCountryData;
  }, [analytics]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || !mapboxToken) return;

    mapboxgl.accessToken = mapboxToken;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      projection: { name: 'mercator' },
      zoom: 1.5,
      center: [0, 20],
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.scrollZoom.disable();

    return () => {
      map.current?.remove();
    };
  }, [mapboxToken]);

  // Update markers when data changes
  useEffect(() => {
    if (!map.current || !analytics) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add new markers
    countryData.current.forEach((count, country) => {
      const coords = countryCoordinates[country] || countryCoordinates['Unknown'];
      if (coords[0] === 0 && coords[1] === 0) return; // Skip unknown locations

      // Create marker element with size based on visitor count
      const el = document.createElement('div');
      const size = Math.min(Math.max(count * 8, 20), 60);
      el.className = 'marker';
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.borderRadius = '50%';
      el.style.background = 'hsl(var(--primary))';
      el.style.opacity = '0.7';
      el.style.border = '2px solid white';
      el.style.cursor = 'pointer';
      el.style.boxShadow = '0 0 20px hsl(var(--primary) / 0.5)';
      el.style.transition = 'all 0.3s ease';

      // Pulse animation
      el.style.animation = 'pulse 2s infinite';

      // Add popup
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
        `<div style="padding: 8px; text-align: center;">
          <strong style="font-size: 16px; display: block; margin-bottom: 4px;">${country}</strong>
          <span style="color: hsl(var(--primary));">${count} visitor${count > 1 ? 's' : ''}</span>
        </div>`
      );

      const marker = new mapboxgl.Marker(el)
        .setLngLat(coords)
        .setPopup(popup)
        .addTo(map.current!);

      markers.current.push(marker);
    });
  }, [analytics]);

  // Add pulse animation to document
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes pulse {
        0%, 100% {
          transform: scale(1);
          opacity: 0.7;
        }
        50% {
          transform: scale(1.1);
          opacity: 0.9;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const totalVisitors = analytics?.length || 0;
  const uniqueCountries = countryData.current.size;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Global Visitor Map
        </CardTitle>
        <CardDescription>
          {totalVisitors} visits from {uniqueCountries} {uniqueCountries === 1 ? 'country' : 'countries'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div 
          ref={mapContainer} 
          className="w-full h-[400px] rounded-lg overflow-hidden border border-border"
          style={{ background: 'hsl(var(--muted))' }}
        />
        
        {!mapboxToken && (
          <div className="mt-4 p-4 bg-muted rounded-lg text-center">
            <p className="text-sm text-muted-foreground">
              Map is loading... Make sure your Mapbox token is configured.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
